import { Menu, Count, usePermissions, useTranslate, useSidebarState } from 'react-admin';
import { Box, Collapse, Divider } from '@mui/material';
import { useState } from 'react';
import TelegramIcon from '@mui/icons-material/Telegram';
import CelebrationIcon from '@mui/icons-material/Celebration';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import StoreIcon from '@mui/icons-material/Store';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExtensionIcon from '@mui/icons-material/Extension';
import ContactsIcon from '@mui/icons-material/Contacts';
import BarChartIcon from '@mui/icons-material/BarChart';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import LabelIcon from '@mui/icons-material/Label';
import TuneIcon from '@mui/icons-material/Tune';
import StyleIcon from '@mui/icons-material/Style';
import CampaignIcon from '@mui/icons-material/Campaign';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalFloristOutlinedIcon from '@mui/icons-material/LocalFloristOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SectionLabel = ({ label, isOpen }: { label: string; isOpen: boolean }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </Box>
);

const CountLabel = ({ labelKey, resource, translate }: { labelKey: string; resource: string; translate: (key: string, options?: any) => string }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{translate(labelKey, 2)}</span>
        <Box sx={{ ml: 1, flexShrink: 0 }}>
            <Count resource={resource} />
        </Box>
    </Box>
);

export const CustomMenu = () => {
    const translate = useTranslate();
    const [sidebarOpen] = useSidebarState();
    const [ordersOpen, setOrdersOpen] = useState(true);
    const [productsOpen, setProductsOpen] = useState(true);
    const [clientsOpen, setClientsOpen] = useState(true);
    const [channelsOpen, setChannelsOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const { permissions, isLoading } = usePermissions();
    const perms: string[] = permissions || [];

    const hasPermission = (permission: string) => perms.includes(permission);

    const hasOrdersAccess = hasPermission('orders_admin');
    const hasProductsAccess = hasPermission('products_admin') || hasPermission('categories_admin') ||
        hasPermission('stock_movements_admin') || hasPermission('product_compositions_admin') ||
        hasPermission('catalog_labels_admin') || hasPermission('customization_options_admin') ||
        hasPermission('manage_attribute_definitions');
    const hasClientsAccess = hasPermission('clients_admin');
    const hasChannelsAccess = hasPermission('telegram_products_admin') || hasPermission('promo_codes_admin') || hasPermission('campaigns_admin');
    const hasSettingsAccess = hasPermission('users_management_admin') || hasPermission('roles_management_admin') ||
        hasPermission('branches_admin') || hasPermission('delivery_slots_admin') || hasPermission('migration_manager_admin') ||
        hasPermission('app_settings_admin') || hasPermission('audit_log_admin');

    if (isLoading) {
        return <Menu />;
    }

    // Collapsed sidebar: flat icon-only list, no section headers, thin dividers between groups
    if (!sidebarOpen) {
        return (
            <Menu>
                {hasPermission('analytics_admin') && (
                    <Menu.Item to="/analytics" primaryText={translate('app.menu.analytics')} leftIcon={<BarChartIcon />} />
                )}

                {hasOrdersAccess && <Divider sx={{ my: 0.5, mx: 1 }} />}
                {hasOrdersAccess && (
                    <Menu.Item to="/manual-orders" primaryText={translate('resources.manual-orders.name', 2)} leftIcon={<ShoppingCartIcon />} />
                )}
                {hasOrdersAccess && (
                    <Menu.Item to="/mobile-orders" primaryText={translate('resources.mobile-orders.name', 2)} leftIcon={<PhoneAndroidIcon />} />
                )}
                {hasPermission('events_admin') && (
                    <Menu.Item to="/events" primaryText={translate('resources.events.name', { smart_count: 2, _: 'Events' })} leftIcon={<CelebrationIcon />} />
                )}

                {hasProductsAccess && <Divider sx={{ my: 0.5, mx: 1 }} />}
                {hasPermission('products_admin') && (
                    <Menu.Item to="/products" primaryText={translate('resources.products.name', 2)} leftIcon={<InventoryIcon />} />
                )}
                {hasPermission('categories_admin') && (
                    <Menu.Item to="/categories" primaryText={translate('resources.categories.name', 2)} leftIcon={<CategoryIcon />} />
                )}
                {hasPermission('product_compositions_admin') && (
                    <Menu.Item to="/product-compositions" primaryText={translate('resources.product-compositions.name', 2)} leftIcon={<ExtensionIcon />} />
                )}
                {hasPermission('stock_movements_admin') && (
                    <Menu.Item to="/stock-movements" primaryText={translate('resources.stock-movements.name', 2)} leftIcon={<SwapVertIcon />} />
                )}
                {hasPermission('customization_options_admin') && (
                    <Menu.Item to="/admin/customization-options" primaryText={translate('resources.admin/customization-options.name', 2)} leftIcon={<TuneIcon />} />
                )}
                {hasPermission('manage_attribute_definitions') && (
                    <Menu.Item to="/attribute-definitions" primaryText={translate('resources.attribute-definitions.name', 2)} leftIcon={<StyleIcon />} />
                )}

                {hasClientsAccess && <Divider sx={{ my: 0.5, mx: 1 }} />}
                {hasClientsAccess && (
                    <Menu.Item to="/clients" primaryText={translate('resources.clients.name', 2)} leftIcon={<PeopleIcon />} />
                )}
                {hasClientsAccess && (
                    <Menu.Item to="/loyalty-transactions" primaryText={translate('resources.loyalty-transactions.name', 2)} leftIcon={<LoyaltyIcon />} />
                )}
                {hasPermission('orders_admin') && (
                    <Menu.Item to="/loyalty/scan" primaryText={translate('app.menu.loyaltyScan')} leftIcon={<LoyaltyIcon />} />
                )}

                {hasChannelsAccess && <Divider sx={{ my: 0.5, mx: 1 }} />}
                {hasPermission('telegram_products_admin') && (
                    <Menu.Item to="/telegram-products" primaryText={translate('resources.telegram-products.name', 2)} leftIcon={<TelegramIcon />} />
                )}
                {hasPermission('promo_codes_admin') && (
                    <Menu.Item to="/promo-codes" primaryText={translate('resources.promo-codes.name', 2)} leftIcon={<LocalOfferIcon />} />
                )}
                {hasPermission('campaigns_admin') && (
                    <Menu.Item to="/campaigns" primaryText={translate('resources.campaigns.name', 2)} leftIcon={<CampaignOutlinedIcon />} />
                )}

                {hasSettingsAccess && <Divider sx={{ my: 0.5, mx: 1 }} />}
                {hasPermission('branches_admin') && (
                    <Menu.Item to="/branches" primaryText={translate('resources.branches.name', 2)} leftIcon={<StoreIcon />} />
                )}
                {hasPermission('delivery_slots_admin') && (
                    <Menu.Item to="/delivery-time-slots" primaryText={translate('resources.delivery-time-slots.name', 2)} leftIcon={<AccessTimeIcon />} />
                )}
                {hasPermission('users_management_admin') && (
                    <Menu.Item to="/users" primaryText={translate('resources.users.name', 2)} leftIcon={<PeopleIcon />} />
                )}
                {hasPermission('roles_management_admin') && (
                    <Menu.Item to="/role-management" primaryText={translate('resources.roles.name', 2)} leftIcon={<SecurityIcon />} />
                )}
                {hasPermission('migration_manager_admin') && (
                    <Menu.Item to="/migration-manager" primaryText={translate('app.menu.dotykacka')} leftIcon={<SyncAltIcon />} />
                )}
                {hasPermission('app_settings_admin') && (
                    <Menu.Item to="/settings" primaryText={translate('resources.admin/settings.name', { _: 'Settings' })} leftIcon={<SettingsIcon />} />
                )}
                {hasPermission('app_settings_admin') && (
                    <Menu.Item to="/bouquet-builder-settings" primaryText={translate('app.bouquetBuilder.menu', { _: 'Bouquet Builder' })} leftIcon={<LocalFloristOutlinedIcon />} />
                )}
                {hasPermission('audit_log_admin') && (
                    <Menu.Item to="/admin-actions" primaryText={translate('resources.admin-actions.name', { _: 'Audit Log' })} leftIcon={<HistoryIcon />} />
                )}
            </Menu>
        );
    }

    // Expanded sidebar: grouped sections with collapse
    return (
        <Menu>
            {/* Analytics — top-level, daily entry point */}
            {hasPermission('analytics_admin') && (
                <Menu.Item
                    to="/analytics"
                    primaryText={translate('app.menu.analytics')}
                    leftIcon={<BarChartIcon />}
                />
            )}

            {/* Orders — highest-frequency daily task */}
            {hasOrdersAccess && (
                <>
                    <Box aria-expanded={ordersOpen}>
                    <Menu.Item
                        to="#"
                        primaryText={<SectionLabel label={translate('app.menu.orders')} isOpen={ordersOpen} />}
                        leftIcon={<ReceiptLongIcon />}
                        onClick={() => setOrdersOpen(!ordersOpen)}
                    />
                    </Box>
                    <Collapse in={ordersOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2 }}>
                            <Menu.Item
                                to="/manual-orders"
                                primaryText={<CountLabel labelKey="resources.manual-orders.name" resource="manual-orders" translate={translate} />}
                                leftIcon={<ShoppingCartIcon />}
                            />
                            <Menu.Item
                                to="/mobile-orders"
                                primaryText={<CountLabel labelKey="resources.mobile-orders.name" resource="mobile-orders" translate={translate} />}
                                leftIcon={<PhoneAndroidIcon />}
                            />
                            {hasPermission('events_admin') && (
                                <Menu.Item
                                    to="/events"
                                    primaryText={<CountLabel labelKey="resources.events.name" resource="events" translate={translate} />}
                                    leftIcon={<CelebrationIcon />}
                                />
                            )}
                        </Box>
                    </Collapse>
                </>
            )}

            {/* Products — product management cluster */}
            {hasProductsAccess && (
                <>
                    <Box aria-expanded={productsOpen}>
                    <Menu.Item
                        to="#"
                        primaryText={<SectionLabel label={translate('app.menu.products')} isOpen={productsOpen} />}
                        leftIcon={<LocalFloristIcon />}
                        onClick={() => setProductsOpen(!productsOpen)}
                    />
                    </Box>
                    <Collapse in={productsOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2 }}>
                            {hasPermission('products_admin') && (
                                <Menu.Item
                                    to="/products"
                                    primaryText={<CountLabel labelKey="resources.products.name" resource="products" translate={translate} />}
                                    leftIcon={<InventoryIcon />}
                                />
                            )}
                            {hasPermission('categories_admin') && (
                                <Menu.Item
                                    to="/categories"
                                    primaryText={<CountLabel labelKey="resources.categories.name" resource="categories" translate={translate} />}
                                    leftIcon={<CategoryIcon />}
                                />
                            )}
                            {hasPermission('product_compositions_admin') && (
                                <Menu.Item
                                    to="/product-compositions"
                                    primaryText={<CountLabel labelKey="resources.product-compositions.name" resource="product-compositions" translate={translate} />}
                                    leftIcon={<ExtensionIcon />}
                                />
                            )}
                            {hasPermission('catalog_labels_admin') && (
                                <Menu.Item
                                    to="/admin/catalog-labels"
                                    primaryText={<CountLabel labelKey="resources.admin/catalog-labels.name" resource="admin/catalog-labels" translate={translate} />}
                                    leftIcon={<LabelIcon />}
                                />
                            )}
                            {hasPermission('stock_movements_admin') && (
                                <Menu.Item
                                    to="/stock-movements"
                                    primaryText={<CountLabel labelKey="resources.stock-movements.name" resource="stock-movements" translate={translate} />}
                                    leftIcon={<SwapVertIcon />}
                                />
                            )}
                            {hasPermission('customization_options_admin') && (
                                <Menu.Item
                                    to="/admin/customization-options"
                                    primaryText={<CountLabel labelKey="resources.admin/customization-options.name" resource="admin/customization-options" translate={translate} />}
                                    leftIcon={<TuneIcon />}
                                />
                            )}
                            {hasPermission('manage_attribute_definitions') && (
                                <Menu.Item
                                    to="/attribute-definitions"
                                    primaryText={<CountLabel labelKey="resources.attribute-definitions.name" resource="attribute-definitions" translate={translate} />}
                                    leftIcon={<StyleIcon />}
                                />
                            )}
                        </Box>
                    </Collapse>
                </>
            )}

            {/* Clients — CRM cluster */}
            {hasClientsAccess && (
                <>
                    <Box aria-expanded={clientsOpen}>
                    <Menu.Item
                        to="#"
                        primaryText={<SectionLabel label={translate('app.menu.clients')} isOpen={clientsOpen} />}
                        leftIcon={<ContactsIcon />}
                        onClick={() => setClientsOpen(!clientsOpen)}
                    />
                    </Box>
                    <Collapse in={clientsOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2 }}>
                            <Menu.Item
                                to="/clients"
                                primaryText={<CountLabel labelKey="resources.clients.name" resource="clients" translate={translate} />}
                                leftIcon={<PeopleIcon />}
                            />
                            <Menu.Item
                                to="/loyalty-transactions"
                                primaryText={translate('resources.loyalty-transactions.name', 2)}
                                leftIcon={<LoyaltyIcon />}
                            />
                            {hasPermission('orders_admin') && (
                                <Menu.Item
                                    to="/loyalty/scan"
                                    primaryText={translate('app.menu.loyaltyScan')}
                                    leftIcon={<LoyaltyIcon />}
                                />
                            )}
                        </Box>
                    </Collapse>
                </>
            )}

            {/* Channels — publishing & marketing */}
            {hasChannelsAccess && (
                <>
                    <Box aria-expanded={channelsOpen}>
                    <Menu.Item
                        to="#"
                        primaryText={<SectionLabel label={translate('app.menu.channels')} isOpen={channelsOpen} />}
                        leftIcon={<CampaignIcon />}
                        onClick={() => setChannelsOpen(!channelsOpen)}
                    />
                    </Box>
                    <Collapse in={channelsOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2 }}>
                            {hasPermission('telegram_products_admin') && (
                                <Menu.Item
                                    to="/telegram-products"
                                    primaryText={<CountLabel labelKey="resources.telegram-products.name" resource="telegram-products" translate={translate} />}
                                    leftIcon={<TelegramIcon />}
                                />
                            )}
                            {hasPermission('promo_codes_admin') && (
                                <Menu.Item
                                    to="/promo-codes"
                                    primaryText={<CountLabel labelKey="resources.promo-codes.name" resource="promo-codes" translate={translate} />}
                                    leftIcon={<LocalOfferIcon />}
                                />
                            )}
                            {hasPermission('campaigns_admin') && (
                                <Menu.Item
                                    to="/campaigns"
                                    primaryText={<CountLabel labelKey="resources.campaigns.name" resource="campaigns" translate={translate} />}
                                    leftIcon={<CampaignOutlinedIcon />}
                                />
                            )}
                        </Box>
                    </Collapse>
                </>
            )}

            {/* Settings — low-frequency admin */}
            {hasSettingsAccess && (
                <>
                    <Box aria-expanded={settingsOpen}>
                    <Menu.Item
                        to="#"
                        primaryText={<SectionLabel label={translate('app.menu.settings')} isOpen={settingsOpen} />}
                        leftIcon={<SettingsIcon />}
                        onClick={() => setSettingsOpen(!settingsOpen)}
                    />
                    </Box>
                    <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 2 }}>
                            {hasPermission('branches_admin') && (
                                <Menu.Item
                                    to="/branches"
                                    primaryText={<CountLabel labelKey="resources.branches.name" resource="branches" translate={translate} />}
                                    leftIcon={<StoreIcon />}
                                />
                            )}
                            {hasPermission('delivery_slots_admin') && (
                                <Menu.Item
                                    to="/delivery-time-slots"
                                    primaryText={<CountLabel labelKey="resources.delivery-time-slots.name" resource="delivery-time-slots" translate={translate} />}
                                    leftIcon={<AccessTimeIcon />}
                                />
                            )}
                            {hasPermission('users_management_admin') && (
                                <Menu.Item
                                    to="/users"
                                    primaryText={<CountLabel labelKey="resources.users.name" resource="users" translate={translate} />}
                                    leftIcon={<PeopleIcon />}
                                />
                            )}
                            {hasPermission('roles_management_admin') && (
                                <Menu.Item
                                    to="/role-management"
                                    primaryText={translate('resources.roles.name', 2)}
                                    leftIcon={<SecurityIcon />}
                                />
                            )}
                            {hasPermission('migration_manager_admin') && (
                                <Menu.Item
                                    to="/migration-manager"
                                    primaryText={translate('app.menu.dotykacka')}
                                    leftIcon={<SyncAltIcon />}
                                />
                            )}
                            {hasPermission('app_settings_admin') && (
                                <Menu.Item
                                    to="/settings"
                                    primaryText={translate('resources.admin/settings.name', { _: 'Settings' })}
                                    leftIcon={<SettingsIcon />}
                                />
                            )}
                            {hasPermission('app_settings_admin') && (
                                <Menu.Item
                                    to="/bouquet-builder-settings"
                                    primaryText={translate('app.bouquetBuilder.menu', { _: 'Bouquet Builder' })}
                                    leftIcon={<LocalFloristOutlinedIcon />}
                                />
                            )}
                            {hasPermission('audit_log_admin') && (
                                <Menu.Item
                                    to="/admin-actions"
                                    primaryText={translate('resources.admin-actions.name', { _: 'Audit Log' })}
                                    leftIcon={<HistoryIcon />}
                                />
                            )}
                        </Box>
                    </Collapse>
                </>
            )}
        </Menu>
    );
};
